/**
 * The hero (the player's look, with and without a weapon, and in the Wudang uniform), the first
 * foes and the elder, and the hero's alternative colourings. Makes `LOOKS`, which the other files
 * here add their own looks to.
 */
import type { Look } from './types';

const SKIN = { skin: '#f7d5b9', skinS: '#e0ae8e', mouth: '#c4766a', eye: '#1d1a2e', brow: '#2a2440' };
const STEEL = { blade: '#eef4ff', bladeS: '#9fb3cf', hilt: '#3a2f4a', guard: '#d9b46c' };

export const heroBase: Look = {
  ...SKIN,
  ...STEEL,
  brow: '#5a2e26',
  iris: '#2fb7c9',
  irisH: '#9af2ee',
  blush: '#f4b8aa',
  hair: '#1c1f30',
  hairH: '#4a5a8a',
  hairStyle: 'bun',
  headwear: 'gem',
  accent: '#2a3f7a',
  crown: '#3fe08a',
  crownS: '#1f9a58',
  outer: '#3f72cf',
  outerS: '#2b519c',
  outerH: '#76a2ec',
  trim: '#45d07e',
  inner: '#f1f2f4',
  innerS: '#c9ced8',
  sash: '#24386e',
  sashS: '#182650',
  pendant: '#3fe08a',
  tassel: '#d23a3a',
  boots: '#2a2438',
  bootsS: '#4c445c',
  weapon: 'sword',
  wlen: 22,
};

/** Hair and headwear from the chosen reference sheet, used for the face comparison. */
const REF_HEAD: Partial<Look> = {
  hair: '#3a241c',
  hairH: '#6e4634',
  hairStyle: 'curtain',
  headwear: 'tiara',
  brow: '#1e120e',
  iris: '#3a2018',
  skin: '#f6cfac',
  skinS: '#d9a481',
};

/** The player: reference-sheet head, expression changes with the action. */
export const hero: Look = {
  ...heroBase,
  ...REF_HEAD,
  faceStyle: 'refSerious',
  poseFaces: { atk: 'refShout', block: 'refGrit', cast: 'refGrit' },
  // The navy coat comes from the starting equipment (see OUTFITS).
  ribbon: '#c8343a',
  scabbard: '#2a2238',
};

/** Wudang disciple uniform: white Taoist robe with black trim and inner robe. */
const WUDANG_ROBE = {
  outer: '#eef0f3',
  outerS: '#b9c0ca',
  outerH: '#ffffff',
  trim: '#262a36',
  inner: '#262a36',
  innerS: '#161922',
  sash: '#262a36',
  sashS: '#14161c',
  pendant: '#f0f0f0',
  tassel: '#262a36',
  boots: '#16161c',
  bootsS: '#33333c',
  // A black vest over the white robe: yin over yang.
  coat: { c: '#2b2f3b', s: '#1b1e27', edge: '#c9ced8', len: 8 },
  ribbon: '#262a36',
  scabbard: '#262a36',
} satisfies Partial<Look>;

/**
 * The player as a woman (lane C0-CHAR, 2026-10-10): the hero with the female body, a high bun with a hairpin, and a
 * woman's robe (the ruqun, coloured by an equipped coat). `lookOf` names it for a woman's save.
 */
const HERO_F: Partial<Look> = {
  female: true,
  beard: undefined,
  // A high bun with a hairpin and the red ribbon (rig/sets.ts HAIR: 'bun' → 'highbun' for a woman).
  hairStyle: 'bun',
  headwear: 'pin',
  crown: '#d9b46c',
  crownS: '#a8843c',
  // The wide-sleeved robe, drawn for a woman as the high-waisted ruqun (rig/sets.ts `womanCut`).
  hanfu: true,
  blush: '#f2a6b0',
  // Tinted lips (rig/faces2 lip colour; lane C0-OUTFIT2X).
  mouth: '#c8404e',
};

export const LOOKS: Record<string, Look> = {
  hero,
  heroPalm: { ...hero, weapon: 'none' },
  heroF: { ...hero, ...HERO_F },
  heroFPalm: { ...hero, ...HERO_F, weapon: 'none' },
  heroWudang: { ...hero, ...WUDANG_ROBE, school: 'mist-peak' },
  heroWudangPalm: { ...hero, ...WUDANG_ROBE, school: 'mist-peak', weapon: 'none' },
  taoist: {
    school: 'mist-peak',
    ...SKIN,
    ...STEEL,
    ...WUDANG_ROBE,
    outer: '#d9dde3',
    outerS: '#a9b0bb',
    brow: '#1a1a20',
    iris: '#2a2018',
    faceStyle: 'refCold',
    poseFaces: { atk: 'refShout', block: 'refGrit', cast: 'refGrit' },
    hair: '#1a1a20',
    hairH: '#3a3a48',
    hairStyle: 'topknot',
    headwear: 'pin',
    accent: '#5e4526',
    crown: '#8b6a3a',
    crownS: '#5e4526',
    weapon: 'sword',
    wlen: 20,
    beard: 'long',
    beardColor: '#2a2a30',
    coat: { c: '#2b2f3b', s: '#1b1e27', edge: '#c9ced8', len: 13 },
  },
  bandit: {
    ...SKIN,
    skin: '#dcaa82',
    skinS: '#b8855f',
    brow: '#2b1f1a',
    hair: '#2b1f1a',
    hairH: '#4f3a2c',
    hairStyle: 'messy',
    faceStyle: 'refSmirk',
    poseFaces: { atk: 'refShout', block: 'refGrit', cast: 'refGrit' },
    iris: '#6b4428',
    irisH: '#a87850',
    headwear: 'band',
    accent: '#b8322c',
    crown: '#000000',
    crownS: '#000000',
    outer: '#7d5a3c',
    outerS: '#5a3f28',
    outerH: '#9a7452',
    trim: '#3b2a1c',
    inner: '#5e5246',
    innerS: '#463c33',
    sash: '#2b2b2b',
    sashS: '#1a1a1a',
    tassel: '#8a6a3a',
    boots: '#2a201a',
    bootsS: '#463628',
    weapon: 'sabre',
    scabbard: '#4a3324',
    pelt: { c: '#a08c70', s: '#6e5e48' },
    wlen: 15,
    blade: '#c9ccd2',
    bladeS: '#7d828c',
    hilt: '#5a3a22',
    guard: '#8a6a3a',
    shortRobe: true,
    sleeveless: true,
    beard: 'stubble',
  },
  boss: {
    ...SKIN,
    skin: '#d4c8dc',
    skinS: '#a99bb5',
    mouth: '#6a2030',
    eye: '#1a0e14',
    iris: '#e02a3a',
    faceStyle: 'refCold',
    poseFaces: { atk: 'refShout', block: 'refGrit', cast: 'refGrit' },
    brow: '#2a2030',
    hair: '#ece8f4',
    hairH: '#ffffff',
    hairStyle: 'loose',
    headwear: 'guan',
    accent: '#b02a35',
    crown: '#3a2c44',
    crownS: '#241b2c',
    outer: '#24202c',
    outerS: '#15121b',
    outerH: '#3c3448',
    trim: '#b02a35',
    inner: '#7a1420',
    innerS: '#560d16',
    sash: '#b02a35',
    sashS: '#7a1420',
    pendant: '#ff4a4a',
    tassel: '#ff4a4a',
    boots: '#100c14',
    bootsS: '#2a2230',
    weapon: 'sword',
    wlen: 26,
    blade: '#2c2236',
    bladeS: '#ff4a4a',
    hilt: '#5a1018',
    guard: '#b02a35',
    scabbard: '#1b1722',
    pauldron: { c: '#2e2838', s: '#b02a35' },
    cape: '#1b1722',
    capeS: '#0f0c14',
    tall: 12,
    evil: true,
  },
  elder: {
    ...SKIN,
    skin: '#ecc6a6',
    skinS: '#cfa284',
    brow: '#a9a39a',
    iris: '#4a3a2a',
    faceStyle: 'refCold',
    poseFaces: { atk: 'refGrit', block: 'refGrit', cast: 'refGrit' },
    hair: '#d8d4cc',
    hairH: '#ffffff',
    hairStyle: 'topknot',
    headwear: 'pin',
    accent: '#8b6a3a',
    crown: '#8b6a3a',
    crownS: '#5e4526',
    outer: '#6f7d5c',
    outerS: '#55614a',
    outerH: '#8a9873',
    trim: '#d2b67a',
    inner: '#ece5d0',
    innerS: '#c9c0a6',
    sash: '#3e3428',
    sashS: '#2a231b',
    pendant: '#7fe0b0',
    tassel: '#c0392b',
    boots: '#2e2820',
    bootsS: '#4a4034',
    weapon: 'staff',
    wlen: 0,
    blade: '#8b6a3a',
    bladeS: '#5e4526',
    hilt: '#5e4526',
    guard: '#7fe0b0',
    beard: 'long',
    beardColor: '#f0eeea',
  },
  red: {
    ...heroBase,
    faceStyle: 'refSmirk',
    iris: '#4a2418',
    brow: '#2a1418',
    poseFaces: { atk: 'refShout', block: 'refGrit', cast: 'refGrit' },
    hairStyle: 'ponytail',
    headwear: 'guan',
    hair: '#16141c',
    hairH: '#3e3a4c',
    accent: '#e8c26a',
    crown: '#e8c26a',
    crownS: '#a8843a',
    outer: '#b8343a',
    outerS: '#86242c',
    outerH: '#d85058',
    trim: '#e8c26a',
    coat: { c: '#2a1418', s: '#1a0a0e', edge: '#e8c26a', len: 10 },
    scabbard: '#2a1418',
    inner: '#f2e6d0',
    innerS: '#cdbfa6',
    sash: '#2a1a1c',
    sashS: '#160d0e',
    pendant: '#e8c26a',
  },
  night: {
    ...heroBase,
    faceStyle: 'refSerious',
    iris: '#2a3550',
    brow: '#1a1820',
    poseFaces: { atk: 'refShout', block: 'refGrit', cast: 'refGrit' },
    hairStyle: 'ponytail',
    hair: '#121218',
    hairH: '#3a3d52',
    headwear: 'band',
    accent: '#9fb3cf',
    outer: '#2e3140',
    outerS: '#1d2030',
    outerH: '#474c63',
    trim: '#9fb3cf',
    scabbard: '#1a1c26',
    hat: { c: '#b89a5e', s: '#8a6e3e' },
    inner: '#5a6078',
    innerS: '#41465a',
    sash: '#9fb3cf',
    sashS: '#6d7f99',
    pendant: '#cfd8e6',
    tassel: '#9fb3cf',
    mask: '#1a1c26',
  },
  lilac: {
    ...heroBase,
    faceStyle: 'refSoft',
    iris: '#5a2a6a',
    brow: '#22101c',
    poseFaces: { atk: 'refShout', block: 'refGrit', cast: 'refGrit' },
    hair: '#3a2232',
    hairH: '#7a4a6a',
    hairStyle: 'buns',
    headwear: 'none',
    accent: '#f2a6c0',
    outer: '#ecc4e8',
    outerS: '#bf93c6',
    outerH: '#fbe6f8',
    trim: '#7b4a9a',
    scabbard: '#7b4a9a',
    ribbon: '#f2a6c0',
    inner: '#fff2fb',
    innerS: '#e6cfe0',
    sash: '#7b4a9a',
    sashS: '#56316e',
    pendant: '#f2a6c0',
    tassel: '#f2a6c0',
    wlen: 18,
    female: true,
    blush: '#f2a6b0',
  },
};
